import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { Box, VBox } from "@ui/box/Box";
import { Error } from "@ui/error/Error";
import { SpinnerProgress } from "@ui/spinner/SpinnerProgress";
import { Table } from "@ui/table/Table";
import { Typo } from "@ui/text/Typo";
import { useEffect, useState } from "react";
import { GD } from "src/GD";

type TEventRow = IEvent & {
	action: string;
	target_type: string;
	user_email?: string;
};

export interface IEventsTableProps {}

const formatAction = (action?: string) => (action ? action.replace(/_/g, " ") : "");

const formatEventData = (value: unknown) => {
	if (!value) return "";
	if (typeof value !== "string") return JSON.stringify(value);

	try {
		return JSON.stringify(JSON.parse(value));
	} catch {
		return value;
	}
};

export const EventsTable = (_props: IEventsTableProps) => {
	useLocale();
	const [project] = GD.REQ_CURRENT_PROJECT.useRequest();
	const [events, setEvents] = useState<TEventRow[]>([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<IError>();

	useEffect(() => {
		if (!project?.id) return;

		let cancelled = false;

		const loadEvents = async () => {
			setBusy(true);
			setError(undefined);

			const response = await GD.REQ_API.request({
				method: "events/list",
				data: {
					limit: 100,
					target_id: project.id,
					target_type: "project",
				},
			});

			if (cancelled) return;

			if (response.error) {
				setError(response.error);
				setEvents([]);
			} else {
				setEvents(Array.isArray(response.data) ? response.data : []);
			}

			setBusy(false);
		};

		loadEvents();

		return () => {
			cancelled = true;
		};
	}, [project?.id]);

	return (
		<Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", minHeight: "300px" }}>
			<SpinnerProgress busy={busy} />
			<Error error={error} />

			{!busy && !error && events.length === 0 && (
				<VBox center sx={{ padding: "32px 20px" }}>
					<Typo variant="caption">{msg("noEventsFound")}</Typo>
				</VBox>
			)}

			{events.length > 0 && (
				<Table<TEventRow>
					order={["id", "created_at", "action", "target_type", "target_id", "user_name", "data"]}
					headers={[
						{ value: "id", gridColumn: "max-content" },
						{ value: "created_at", gridColumn: "max-content" },
						{ value: "action", gridColumn: "minmax(160px, max-content)" },
						{ value: "target_type", gridColumn: "max-content" },
						{ value: "target_id", gridColumn: "max-content" },
						{ value: "user_name", gridColumn: "minmax(120px, max-content)" },
						{ value: "data", gridColumn: "minmax(240px, 1fr)" },
					]}
					data={events}
					cellFactory={(row, cell, _index, _key, name) => {
						if (name === "action") return { content: formatAction(cell) };
						if (name === "data") {
							return {
								content: formatEventData(row.data),
								sx: {
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									maxWidth: "100%",
								},
							};
						}

						return { content: cell };
					}}
				/>
			)}
		</Box>
	);
};
